import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api';
import AlertModal from '../components/AlertModal';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [favorites, setFavorites] = useState([]);

    // Global Alert State
    const [alertConfig, setAlertConfig] = useState({
        isOpen: false, title: '', message: '', type: 'info',
        onConfirm: null, showCancel: false, confirmText: 'OK', onCancel: null
    });

    const showAlert = (title, message, type = 'info', onConfirm = null, showCancel = false, confirmText = 'OK') => {
        setAlertConfig({ isOpen: true, title, message, type, onConfirm, showCancel, confirmText });
    };

    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const userData = await api.getMe();
                setUser(userData);
                setFavorites(userData.favorites || []);
            } catch (error) {
                api.logout();
            }
        }
        setLoading(false);
    };

    const login = async (email, password) => {
        const data = await api.login(email, password);
        setUser(data.user);
        setFavorites(data.user.favorites || []);
        return data.user;
    };

    const register = async (email, password, name, agreedToTerms = false) => {
        const data = await api.register(email, password, name, agreedToTerms);
        setUser(data.user);
        setFavorites([]);
        return data.user;
    };

    const logout = () => {
        api.logout();
        setUser(null);
        setFavorites([]);
        // Force full page reload to home — clears all stale React state instantly
        window.location.href = '/';
    };

    const toggleFavorite = async (eventId) => {
        if (!user) return false;

        try {
            const favoritesArray = Array.isArray(favorites) ? favorites : [];
            if (favoritesArray.includes(eventId)) {
                await api.removeFavorite(eventId);
                setFavorites(prev => (Array.isArray(prev) ? prev : []).filter(id => id !== eventId));
            } else {
                await api.addFavorite(eventId);
                setFavorites(prev => [...(Array.isArray(prev) ? prev : []), eventId]);
            }
            return true;
        } catch (error) {
            console.error('Favorite error:', error);
            return false;
        }
    };

    const isFavorite = (eventId) => Array.isArray(favorites) && favorites.includes(eventId);

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            login,
            register,
            logout,
            favorites,
            toggleFavorite,
            isFavorite,
            isAdmin: user?.is_admin || false,
            isPartner: user?.is_partner || false,
            showAlert
        }}>
            {children}
            <AlertModal
                isOpen={alertConfig.isOpen}
                onClose={() => setAlertConfig({ ...alertConfig, isOpen: false })}
                title={alertConfig.title}
                message={alertConfig.message}
                type={alertConfig.type}
                onConfirm={alertConfig.onConfirm}
                showCancel={alertConfig.showCancel}
                confirmText={alertConfig.confirmText}
            />
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}
